import { useCallback, useMemo, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.svg";
import { useAuditLogStore } from "@/store/audit-logs";

const ACTOR_NAV_ITEMS: Array<{ label: string; value?: string }> = [
    { label: "All", value: undefined },
    { label: "Evalion", value: "EVALION" },
    { label: "Stripe", value: "STRIPE" },
    { label: "Unknown", value: "UNKNOWN" },
    { label: "User", value: "USER" },
];

const OUTCOME_OPTIONS: Array<{ label: string; value?: boolean }> = [
    { label: "Outcome: all", value: undefined },
    { label: "Outcome: success", value: true },
    { label: "Outcome: failure", value: false },
];

export const Sidebar = () => {
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
        outcome: true,
        actors: true,
        actions: true,
        models: true,
    });

    const actorType = useAuditLogStore((state) => state.filters.actorType);
    const successFilter = useAuditLogStore((state) => state.filters.success);
    const modelFilter = useAuditLogStore((state) => state.filters.model);
    const actionFilter = useAuditLogStore((state) => state.filters.action);
    const targetId = useAuditLogStore((state) => state.filters.targetId);
    const setActorType = useAuditLogStore((state) => state.setActorType);
    const setSuccess = useAuditLogStore((state) => state.setSuccess);
    const setModel = useAuditLogStore((state) => state.setModel);
    const setAction = useAuditLogStore((state) => state.setAction);
    const models = useAuditLogStore((state) => state.models);
    const modelsLoading = useAuditLogStore((state) => state.modelsLoading);
    const actions = useAuditLogStore((state) => state.actions);
    const actionsLoading = useAuditLogStore((state) => state.actionsLoading);
    const sortedActions = useMemo(
        () => [...actions].sort((a, b) => a.localeCompare(b)),
        [actions],
    );
    const sortedModels = useMemo(
        () => [...models].sort((a, b) => a.localeCompare(b)),
        [models],
    );

    const toTitleCase = useCallback((input: string) => {
        return input
            .replace(/[:]/g, " ")
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .split(/[\s_-]+/)
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
            .join(" ");
    }, []);

    const toggleSection = useCallback((key: string) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    }, []);

    const fetchModels = useAuditLogStore((state) => state.fetchModels);
    const fetchActions = useAuditLogStore((state) => state.fetchActions);

    useEffect(() => {
        fetchModels().catch(() => null);
        fetchActions().catch(() => null);
    }, [fetchModels, fetchActions]);

    const handleSelectActor = useCallback(
        (value?: string) => () => {
            setActorType(value);
        },
        [setActorType],
    );

    const handleSelectOutcome = useCallback(
        (value?: boolean) => () => {
            setSuccess(value);
        },
        [setSuccess],
    );

    const handleSelectModel = useCallback(
        (value?: string) => () => {
            setModel(value);
        },
        [setModel],
    );

    const handleSelectAction = useCallback(
        (value?: string) => () => {
            setAction(value);
        },
        [setAction],
    );

    const activeSummary = useMemo(() => {
        const parts: string[] = [];
        const actorMatch = ACTOR_NAV_ITEMS.find((item) => item.value === actorType);
        parts.push(`Actor: ${toTitleCase(actorMatch?.label ?? "All")}`);
        if (successFilter === true) {
            parts.push(`Outcome: ${toTitleCase("Success")}`);
        } else if (successFilter === false) {
            parts.push(`Outcome: ${toTitleCase("Failure")}`);
        } else {
            parts.push(`Outcome: ${toTitleCase("All")}`);
        }
        parts.push(`Model: ${modelFilter ? toTitleCase(modelFilter) : toTitleCase("All")}`);
        parts.push(`Action: ${actionFilter ? toTitleCase(actionFilter) : toTitleCase("All")}`);
        if (targetId.trim()) {
            parts.push(`ID filter: ${targetId.trim()}`);
        }
        return parts.join(" • ");
    }, [actorType, successFilter, modelFilter, actionFilter, targetId, toTitleCase]);

    return (
        <aside className="sticky top-0 hidden h-screen w-[346px] shrink-0 border-r border-border/70 bg-sidebar-background/70 text-sidebar-foreground shadow-sm lg:flex lg:flex-col">
            <div className="flex h-20 flex-col items-start justify-center gap-1 border-b border-border/70 px-6">
                <img src={logo} alt="Evalion" className="h-6 w-auto" />
                <p className="text-sm font-semibold text-primary">Evalion Data Audit Logs</p>
            </div>
            <nav className="flex flex-1 flex-col px-3 py-6 gap-6 overflow-y-auto">
                <div>
                    <button
                        type="button"
                        onClick={() => toggleSection("outcome")}
                        className="flex w-full items-center justify-between px-3 pb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70"
                    >
                        Outcome
                        <span>{collapsedSections.outcome ? "+" : "−"}</span>
                    </button>
                    {!collapsedSections.outcome && (
                        <ul className="flex flex-col gap-1 text-[0.9rem] font-medium text-muted-foreground">
                            {OUTCOME_OPTIONS.map((option) => {
                                const isActive = successFilter === option.value;
                                return (
                                    <li key={option.label}>
                                        <button
                                            type="button"
                                            onClick={handleSelectOutcome(option.value)}
                                            className={cn(
                                                "flex w-full items-center justify-between rounded-xl px-4 py-2 transition",
                                                isActive
                                                    ? "bg-sidebar-accent text-sidebar-accent-foreground shadow"
                                                    : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                            )}
                                        >
                                    <span>{toTitleCase(option.label)}</span>
                                            {isActive && (
                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-accent-foreground/80">
                                                    Active
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <div>
                    <button
                        type="button"
                        onClick={() => toggleSection("actors")}
                        className="flex w-full items-center justify-between px-3 pb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70"
                    >
                        Actors
                        <span>{collapsedSections.actors ? "+" : "−"}</span>
                    </button>
                    {!collapsedSections.actors && (
                        <ul className="flex flex-col gap-1 text-[0.9rem] font-medium text-muted-foreground">
                            {ACTOR_NAV_ITEMS.map((item) => {
                                const isActive = actorType ? item.value === actorType : item.value === undefined;
                                return (
                                    <li key={item.label}>
                                        <button
                                            type="button"
                                            onClick={handleSelectActor(item.value)}
                                            className={cn(
                                                "flex w-full items-center justify-between rounded-xl px-4 py-2 transition",
                                                isActive
                                                    ? "bg-sidebar-accent text-sidebar-accent-foreground shadow"
                                                    : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                            )}
                                        >
                                            <span>{toTitleCase(item.label)}</span>
                                            {isActive && (
                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-accent-foreground/80">
                                                    Active
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <div>
                    <button
                        type="button"
                        onClick={() => toggleSection("actions")}
                        className="flex w-full items-center justify-between px-3 pb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70"
                    >
                        Actions
                        <span>{collapsedSections.actions ? "+" : "−"}</span>
                    </button>
                    {!collapsedSections.actions && (
                        <ul className="flex flex-col gap-1 text-[0.9rem] font-medium text-muted-foreground">
                            <li>
                                <button
                                    type="button"
                                    onClick={handleSelectAction(undefined)}
                                    className={cn(
                                        "flex w-full items-center justify-between rounded-xl px-4 py-2 transition",
                                        !actionFilter
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground shadow"
                                            : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                    )}
                                >
                                    <span>{toTitleCase("all")}</span>
                                    {!actionFilter && (
                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-accent-foreground/80">
                                            Active
                                        </span>
                                    )}
                                </button>
                            </li>
                            {actionsLoading && (
                                <li className="px-4 py-2 text-sm text-muted-foreground/70">Loading actions…</li>
                            )}
                            {!actionsLoading &&
                                sortedActions.map((action) => {
                                    const isActive = actionFilter === action;
                                    return (
                                        <li key={action}>
                                            <button
                                                type="button"
                                                onClick={handleSelectAction(action)}
                                                className={cn(
                                                    "flex w-full items-center justify-between rounded-xl px-4 py-2 transition",
                                                    isActive
                                                        ? "bg-sidebar-accent text-sidebar-accent-foreground shadow"
                                                        : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                                )}
                                            >
                                                <span>{toTitleCase(action)}</span>
                                                {isActive && (
                                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-accent-foreground/80">
                                                        Active
                                                    </span>
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                        </ul>
                    )}
                </div>
                <div>
                    <button
                        type="button"
                        onClick={() => toggleSection("models")}
                        className="flex w-full items-center justify-between px-3 pb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70"
                    >
                        Models
                        <span>{collapsedSections.models ? "+" : "−"}</span>
                    </button>
                    {!collapsedSections.models && (
                        <ul className="flex flex-col gap-1 text-[0.9rem] font-medium text-muted-foreground">
                            <li>
                                <button
                                    type="button"
                                    onClick={handleSelectModel(undefined)}
                                    className={cn(
                                        "flex w-full items-center justify-between rounded-xl px-4 py-2 transition",
                                        !modelFilter
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground shadow"
                                            : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                    )}
                                >
                                    <span>{toTitleCase("all")}</span>
                                    {!modelFilter && (
                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-accent-foreground/80">
                                            Active
                                        </span>
                                    )}
                                </button>
                            </li>
                            {modelsLoading && (
                                <li className="px-4 py-2 text-sm text-muted-foreground/70">Loading models…</li>
                            )}
                            {!modelsLoading &&
                                sortedModels.map((model) => {
                                    const isActive = modelFilter === model;
                                    return (
                                        <li key={model}>
                                            <button
                                                type="button"
                                                onClick={handleSelectModel(model)}
                                                className={cn(
                                                    "flex w-full items-center justify-between rounded-xl px-4 py-2 transition",
                                                    isActive
                                                        ? "bg-sidebar-accent text-sidebar-accent-foreground shadow"
                                                        : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                                )}
                                            >
                                                <span>{toTitleCase(model)}</span>
                                                {isActive && (
                                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-accent-foreground/80">
                                                        Active
                                                    </span>
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                        </ul>
                    )}
                </div>
                <div className="mt-auto rounded-lg border border-border/70 bg-secondary/40 px-4 py-5 text-sm text-muted-foreground/80">
                    <p className="font-semibold text-muted-foreground/90">Currently viewing</p>
                    <p className="text-base font-semibold text-primary">
                        {activeSummary}
                    </p>
                </div>
            </nav>
        </aside>
    );
};
